import type {
  DatasetTriggerCriterion,
  DatasetTriggerRunMode,
} from '@cotera/sdk/core';
import {
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEntityNodeModel } from './workflow-entity-node-model';

export type DatasetTriggerNodeSnapshot = {
  readonly id: string;
  readonly name: string;
  readonly datasetId: string;
  readonly criterion: DatasetTriggerCriterion;
  /** Null watches every non-internal column, resolved when the trigger runs. */
  readonly trackFields: readonly string[] | null;
  readonly processExistingRows: boolean;
  /** `manual` never fires on its own; it runs from the dataset's Run menu. */
  readonly runMode: DatasetTriggerRunMode;
  readonly enabled: boolean;
};

/**
 * Per-node view model for dataset triggers on the workflow canvas. State is
 * derived from the workflow graph snapshot (updated when the draft graph syncs).
 */
export class WorkflowDatasetTriggerNodeModel extends WorkflowEntityNodeModel {
  readonly triggerId: string;
  readonly snapshotWatchable: ReadonlyWatchable<DatasetTriggerNodeSnapshot>;
  readonly datasetIdWatchable: ReadonlyWatchable<string>;
  readonly criterionWatchable: ReadonlyWatchable<DatasetTriggerCriterion>;

  private readonly _snapshotWatchable: Watchable<DatasetTriggerNodeSnapshot>;

  constructor(triggerId: string, snapshot: DatasetTriggerNodeSnapshot) {
    super(triggerId);
    this.triggerId = triggerId;
    this._snapshotWatchable = Watchable.fromValue(snapshot);
    this.snapshotWatchable = this._snapshotWatchable;
    this.datasetIdWatchable = Watchable.from(
      (get) => get(this._snapshotWatchable).datasetId
    );
    this.criterionWatchable = Watchable.from(
      (get) => get(this._snapshotWatchable).criterion
    );
  }

  updateSnapshot(snapshot: DatasetTriggerNodeSnapshot): void {
    this._snapshotWatchable.set(snapshot);
  }

  dispose(): void {}
}
